import type { ComponentProps } from "react"
import { formatDateString } from "@/v2-common/utils/utils.date"
import { formatNumber } from "@/v2-common/utils/utils.formatting"
import cn from "@/v2-ui/utils/utils.cn"
import Badge from "@/v2-backoffice-client/components/badge"
import Link from "@/v2-backoffice-client/components/link"
import RelativeDate from "@/v2-backoffice-client/components/RelativeDate"
import UsageTrendBadge from "@/v2-backoffice-client/usage/UsageTrendBadge"
import { formatTrendDetail } from "@/v2-backoffice-client/usage/usage.constants"
import type { UsageMcpCustomerQuery } from "@/v2-backoffice-client/usage/mcp/__types__/usage.mcpCustomer.query"
import { formatLicenseDeniedReason } from "@/v2-backoffice-client/usage/denied/usage.denied.constants"
import {
  formatMcpClient,
  formatMcpTool,
} from "@/v2-backoffice-client/usage/mcp/usage.mcp.constants"
import { mcpTrendPct } from "@/v2-backoffice-client/usage/mcp/usage.mcp.utils"

export type UsageMcpMember =
  UsageMcpCustomerQuery["getUsageMcpByCustomer"]["Members"][number]

export type UsageMcpMembersTableProps = ComponentProps<"div"> & {
  members: UsageMcpMember[]
}

const CLIENT_LIST_LIMIT = 3

function memberName(member: UsageMcpMember) {
  const name = `${member.FirstName ?? ""} ${member.LastName ?? ""}`.trim()
  return name || member.Email || `User ${member.UserId}`
}

// Who at the customer has taken the MCP into use: one row per person with
// 28-day calls and trend, the last 7 days, active days, the AI clients they
// call from, their top tool, last call and — for people without a seat —
// the refused sign-ins. ">>" pushes the user panel onto the sheet stack so
// back returns here. Scrolls sideways in a narrow sheet rather than
// wrapping its numbers.
function UsageMcpMembersTable(props: UsageMcpMembersTableProps) {
  const { members, className, ...divProps } = props

  return (
    <div className={cn("overflow-x-auto", className)} {...divProps}>
      <table className="usage-report-table">
        <thead>
          <tr>
            <th>Person</th>
            <th className="num">Calls · 28 d</th>
            <th className="num">Last 7 d</th>
            <th
              className="num"
              title="Days with at least one tool call in the last 28"
            >
              Active days
            </th>
            <th>AI clients</th>
            <th>Top tool</th>
            <th>Last call</th>
            <th>Refused</th>
          </tr>
        </thead>
        <tbody>
          {members.map((member) => {
            const trendPct = mcpTrendPct(member.Calls28d, member.CallsPrev28d)
            const topTool = member.TopTools[0]
            return (
              <tr key={member.UserId}>
                <td>
                  <Link
                    variant="muted"
                    size="xs"
                    iconSrc={null}
                    to={`>>?v=user:${member.UserId}&user-view-tabs-tab=usage`}
                    className="font-medium text-foreground underline-offset-2 hover:underline"
                    title={
                      member.FirstActiveAt
                        ? `First call ${formatDateString(member.FirstActiveAt)}`
                        : undefined
                    }
                  >
                    {memberName(member)}
                  </Link>
                  {member.Email && (
                    <span className="block max-w-56 truncate text-muted-foreground">
                      {member.Email}
                    </span>
                  )}
                </td>
                <td className="num">
                  <span className="inline-flex items-center justify-end gap-1.5">
                    {member.Calls28d > 0 ? formatNumber(member.Calls28d) : "—"}
                    {(member.Calls28d > 0 || member.CallsPrev28d > 0) && (
                      <UsageTrendBadge
                        trendPct={trendPct}
                        detail={formatTrendDetail(
                          member.Calls28d,
                          member.CallsPrev28d,
                        )}
                      />
                    )}
                  </span>
                </td>
                <td className="num">
                  {member.Calls7d > 0 ? (
                    formatNumber(member.Calls7d)
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </td>
                <td className="num">
                  {member.ActiveDays28d > 0 ? member.ActiveDays28d : "—"}
                </td>
                <td>
                  {member.Clients.length > 0
                    ? member.Clients.slice(0, CLIENT_LIST_LIMIT)
                        .map(formatMcpClient)
                        .join(", ")
                    : "—"}
                </td>
                <td className="max-w-44">
                  {topTool ? (
                    <span className="block truncate" title={topTool.Tool}>
                      {formatMcpTool(topTool.Tool)}
                      <span className="text-muted-foreground">
                        {" "}
                        · {formatNumber(topTool.Calls)}
                      </span>
                    </span>
                  ) : (
                    "—"
                  )}
                </td>
                <td className="whitespace-nowrap text-muted-foreground">
                  {member.LastActiveAt ? (
                    <RelativeDate date={member.LastActiveAt} />
                  ) : (
                    "—"
                  )}
                </td>
                <td>
                  {member.Denied28d > 0 ? (
                    <Badge
                      variant="warning"
                      size="xs"
                      tooltip={`${formatNumber(member.Denied28d)} refused sign-in${member.Denied28d === 1 ? "" : "s"} in 28 days · ${formatLicenseDeniedReason(member.DeniedReason)}${member.LastDeniedAt ? ` · last ${formatDateString(member.LastDeniedAt)}` : ""}`}
                    >
                      {formatNumber(member.Denied28d)} ×{" "}
                      {formatLicenseDeniedReason(member.DeniedReason)}
                    </Badge>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

export default UsageMcpMembersTable
